import { Link, Navigate, useLocation } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { ArrowRight, CheckCircle2, ChevronLeft, FileText, MapPin, Shield, Sparkles } from 'lucide-react';
import { Button } from '../components/ui/button';
import SeoMeta from '../components/SeoMeta';
import PublicPageLayout from '../components/layout/PublicPageLayout';
import { SEO_LANDINGS } from '../data/seoLandingsContent';
import { buildBreadcrumbJsonLd } from '../data/seoContent';

/**
 * Dedicated SEO landing pages targeting the two most competitive French
 * commercial queries on the drone compliance market:
 *   /preparation-vol-drone  → "préparation vol drone"
 *   /mission-drone          → "mission drone"
 *
 * Each page injects three JSON-LD blocks (HowTo + FAQPage + BreadcrumbList)
 * for maximum SERP eligibility (rich results, How-To carousel, FAQ snippets).
 */
export default function SeoLandingPage() {
  const location = useLocation();
  // Strip any /fr language prefix and lookup the slug
  const slug = location.pathname.replace(/^\/(fr|en|de|it|es|ca)/, '').replace(/^\//, '');
  const content = SEO_LANDINGS[slug];
  const [openFaq, setOpenFaq] = useState(null);

  // Inject <link rel="alternate" hreflang> pairs directly into <head> so
  // Google understands the FR/ES Andorra pages are the same content in two
  // languages. We manage the DOM manually here (matching SeoMeta's approach)
  // to avoid conflicts with the existing hreflang manager. Cleanup on
  // unmount removes only the tags we added. Kept above the `!content`
  // early return to comply with the rules of hooks.
  useEffect(() => {
    if (!content) return undefined;
    const marker = 'data-dronemaps-landing-alt';
    document.querySelectorAll(`link[${marker}]`).forEach((el) => el.remove());
    if (content.hreflangAlternate) {
      const inject = (hreflang, altSlug) => {
        const link = document.createElement('link');
        link.rel = 'alternate';
        link.hreflang = hreflang;
        link.href = `https://drone-maps.eu/${altSlug}`;
        link.setAttribute(marker, '1');
        document.head.appendChild(link);
      };
      inject(content.lang || 'fr', content.slug);
      Object.entries(content.hreflangAlternate).forEach(([lg, alt]) => inject(lg, alt));
      const defaultSlug = (content.lang || 'fr') === 'fr'
        ? content.slug
        : content.hreflangAlternate.fr || content.slug;
      inject('x-default', defaultSlug);
    }
    return () => {
      document.querySelectorAll(`link[${marker}]`).forEach((el) => el.remove());
    };
  }, [content]);

  if (!content) {
    return <Navigate to="/" replace />;
  }

  // Language-aware UI strings. Landings created before i18n was needed
  // default to French (that's how they shipped). New localised landings
  // (e.g. the Spanish Andorra page) supply a `content.ui` block that
  // overrides every visible string on the page.
  const lang = content.lang || 'fr';
  const ui = {
    ctaPrimary:        'Démarrer une mission',
    ctaMap:            'Voir la carte interactive',
    noMapNotice:       "Ce pays n'est pas encore cartographié — mesurez l'intérêt en vous inscrivant, on prévient dès que c'est prêt.",
    stepsTitle:        'La méthode Drone Maps, étape par étape',
    useCasesTitle:     "Cas d'usage concrets",
    faqTitle:          'Questions fréquentes',
    countriesTitle:    'Réglementation par pays',
    countriesSubtitle: 'Préparez votre vol ou votre mission drone dans chacun des 31 pays européens couverts par Drone Maps.',
    breadcrumbHome:    'Drone Maps',
    ...(content.ui || {}),
  };
  const canonicalUrl = `https://drone-maps.eu/${content.slug}`;

  const howToJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'HowTo',
    name: content.h1,
    description: content.intro,
    inLanguage: lang,
    totalTime: 'PT15M',
    step: content.steps.map((s, idx) => ({
      '@type': 'HowToStep',
      position: idx + 1,
      name: s.title,
      text: s.body,
      url: `${canonicalUrl}#step-${idx + 1}`,
    })),
  };

  const faqJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: content.faq.map((q) => ({
      '@type': 'Question',
      name: q.question,
      acceptedAnswer: { '@type': 'Answer', text: q.answer },
    })),
  };

  const breadcrumbJsonLd = buildBreadcrumbJsonLd([
    { name: ui.breadcrumbHome, url: 'https://drone-maps.eu/' },
    { name: content.h1.split(' :')[0], url: canonicalUrl },
  ]);

  const publicHeader = (
    <header className="bg-[#1b2236] text-white border-b border-slate-700">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          <Link to="/" className="flex items-center gap-2" data-testid="seo-landing-header-logo">
            <img src="/logo-landscape.png" alt="Drone Maps" className="h-14 md:h-16 w-auto" />
          </Link>
          <nav className="hidden md:flex items-center gap-8">
            <Link to="/" className="text-slate-300 hover:text-white text-sm font-medium">Accueil</Link>
            <Link to="/blog" className="text-slate-300 hover:text-white text-sm font-medium">Blog</Link>
            <Link to="/contact" className="text-slate-300 hover:text-white text-sm font-medium">Contact</Link>
          </nav>
          <Link to="/register">
            <Button className="bg-sky-500 hover:bg-sky-600 text-white font-semibold" data-testid="seo-landing-header-cta">
              Commencer gratuitement
            </Button>
          </Link>
        </div>
      </div>
    </header>
  );

  return (
    <PublicPageLayout publicHeader={publicHeader} title={content.h1}>
      <div className="min-h-screen bg-slate-50">
        <SeoMeta
          title={content.title}
          description={content.metaDescription}
          keywords={content.keywords}
          type="article"
          rawTitle
          jsonLd={[howToJsonLd, faqJsonLd, breadcrumbJsonLd]}
        />

        {/* Hero */}
        <section className="bg-[#1b2236] py-16 text-white">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-sky-400 hover:text-sky-300 text-sm mb-6"
              data-testid="seo-landing-back"
            >
              <ChevronLeft className="w-4 h-4" /> Drone Maps
            </Link>
            <div className="flex items-center gap-3 mb-4 flex-wrap">
              <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-sky-500/10 border border-sky-500/30 text-sky-400 text-sm font-medium">
                <Shield className="w-4 h-4" /> EU 2019/947
              </span>
              <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 text-sm font-medium">
                <MapPin className="w-4 h-4" /> 31 pays couverts
              </span>
              <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-amber-500/10 border border-amber-500/30 text-amber-400 text-sm font-medium">
                <Sparkles className="w-4 h-4" /> Outil officiel ED-269/ED-318
              </span>
            </div>
            <h1
              className="text-4xl sm:text-5xl lg:text-6xl font-bold font-[Barlow] tracking-tight leading-tight mb-6"
              data-testid="seo-landing-h1"
            >
              {content.h1}
            </h1>
            <p className="text-lg text-slate-300 max-w-3xl mb-8">{content.intro}</p>
            <div className="flex flex-wrap gap-3">
              <Link to="/register">
                <Button size="lg" className="bg-sky-500 hover:bg-sky-600 text-white font-semibold" data-testid="seo-landing-hero-cta">
                  {ui.ctaPrimary} <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
              {content.noMapCoverage ? (
                <div
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-md border border-amber-500/40 bg-amber-500/10 text-amber-200 text-sm"
                  data-testid="seo-landing-no-map-notice"
                >
                  <span className="inline-block w-2 h-2 rounded-full bg-amber-400 animate-pulse" />
                  {ui.noMapNotice}
                </div>
              ) : (
                <Link to="/#carte">
                  <Button size="lg" variant="outline" className="border-slate-600 text-slate-200 hover:bg-slate-800">
                    {ui.ctaMap}
                  </Button>
                </Link>
              )}
            </div>
          </div>
        </section>

        {/* Steps */}
        <section className="py-16 bg-white">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl sm:text-3xl font-bold font-[Barlow] text-slate-900 mb-10">
              {ui.stepsTitle}
            </h2>
            <div className="space-y-8">
              {content.steps.map((step, idx) => (
                <article
                  key={idx}
                  id={`step-${idx + 1}`}
                  className="flex gap-5 sm:gap-6 items-start"
                  data-testid={`seo-landing-step-${idx + 1}`}
                >
                  <div className="flex-shrink-0 w-12 h-12 rounded-full bg-sky-500 text-white font-bold text-lg flex items-center justify-center">
                    {idx + 1}
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl sm:text-2xl font-bold font-[Barlow] text-slate-900 mb-3">
                      {step.title}
                    </h3>
                    <p className="text-slate-700 leading-relaxed">{step.body}</p>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* Use cases */}
        <section className="py-16 bg-slate-50">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl sm:text-3xl font-bold font-[Barlow] text-slate-900 mb-10">
              {ui.useCasesTitle}
            </h2>
            <div className="grid sm:grid-cols-2 gap-6">
              {content.useCases.map((uc, idx) => (
                <article
                  key={idx}
                  className="bg-white border border-slate-200 rounded-lg p-6 hover:border-sky-300 hover:shadow-md transition-all"
                  data-testid={`seo-landing-usecase-${idx}`}
                >
                  <div className="flex items-start gap-3 mb-3">
                    <CheckCircle2 className="w-5 h-5 text-sky-500 flex-shrink-0 mt-1" />
                    <h3 className="text-lg font-bold font-[Barlow] text-slate-900">{uc.title}</h3>
                  </div>
                  <p className="text-slate-700 text-sm leading-relaxed">{uc.body}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-16 bg-white">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl sm:text-3xl font-bold font-[Barlow] text-slate-900 mb-8">
              {ui.faqTitle}
            </h2>
            <div className="space-y-3">
              {content.faq.map((item, idx) => (
                <div key={idx} className="bg-slate-50 border border-slate-200 rounded-lg overflow-hidden">
                  <button
                    onClick={() => setOpenFaq(openFaq === idx ? null : idx)}
                    className="w-full flex items-center justify-between p-4 text-left hover:bg-slate-100 transition-colors"
                    data-testid={`seo-landing-faq-toggle-${idx}`}
                  >
                    <span className="font-semibold text-slate-900 pr-3">{item.question}</span>
                    <span className="text-sky-500 text-2xl leading-none flex-shrink-0">
                      {openFaq === idx ? '−' : '+'}
                    </span>
                  </button>
                  {openFaq === idx && (
                    <div className="px-4 pb-4 text-slate-700 leading-relaxed">{item.answer}</div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Related country pages — strong internal linking for SEO */}
        <section className="py-16 bg-slate-50 border-t border-slate-200">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl font-bold font-[Barlow] text-slate-900 mb-3">
              {ui.countriesTitle}
            </h2>
            <p className="text-slate-600 mb-8">
              {ui.countriesSubtitle}
            </p>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {content.relatedCountries.map((c) => (
                <Link
                  key={c.slug}
                  to={`/regulations/${c.slug}`}
                  className="flex items-center justify-between gap-3 p-4 bg-white border border-slate-200 rounded-lg hover:border-sky-300 hover:bg-sky-50 transition-all group"
                  data-testid={`seo-landing-country-${c.slug}`}
                >
                  <div>
                    <div className="font-semibold text-slate-900 group-hover:text-sky-700">{c.name}</div>
                    <div className="text-xs text-slate-500">{c.tag}</div>
                  </div>
                  <ArrowRight className="w-4 h-4 text-slate-400 group-hover:text-sky-500" />
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="bg-[#1b2236] text-white py-20">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <FileText className="w-12 h-12 text-sky-400 mx-auto mb-4" />
            <h2 className="text-3xl sm:text-4xl font-bold font-[Barlow] mb-4">
              Prêt à préparer votre prochaine mission ?
            </h2>
            <p className="text-slate-300 mb-8 text-lg">
              Drone Maps croise votre mission avec les zones ED-269 officielles, vérifie les NOTAM, calcule les autorités à contacter et génère votre rapport PDF en moins de 10 secondes. Gratuit pour les 3 premières missions.
            </p>
            <Link to="/register">
              <Button size="lg" className="bg-sky-500 hover:bg-sky-600 text-white font-semibold px-8" data-testid="seo-landing-cta-bottom">
                Commencer gratuitement <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
          </div>
        </section>

        {/* Mini footer */}
        <footer className="bg-[#1b2236] text-slate-400 py-8 border-t border-slate-700">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-wrap items-center justify-between gap-4 text-sm">
            <p>© Drone Maps — Conformité drone européenne EU 2019/947</p>
            <div className="flex gap-4">
              <Link to="/mentions-legales" className="hover:text-white">Mentions légales</Link>
              <Link to="/cgu" className="hover:text-white">CGU</Link>
              <Link to="/confidentialite" className="hover:text-white">Confidentialité</Link>
            </div>
          </div>
        </footer>
      </div>
    </PublicPageLayout>
  );
}
